import {
  isPast,
  isFuture,
  isValid,
  parseISO,
  formatDistanceToNowStrict,
} from "date-fns";
import { constructCurrency } from "common/currency";
import type { DepositLimit } from "@/hooks/firestore/user/useDepositLimits";

const adjustLimitMessage = "Adjust your limits in your Preferences";
const depositLimitReachedPrefix = "You can deposit";

export const isDepositLimitReachedMessage = (message?: string) =>
  Boolean(message?.startsWith(depositLimitReachedPrefix));

export const getMaximumDepositAmount = (
  limit: DepositLimit | undefined,
  maxAmountOverrideCents?: number,
) => {
  if (limit) {
    const expiry = limit.expiresAt && parseISO(limit.expiresAt);
    const hasNotExpired = expiry && isFuture(expiry);
    const maxDepositAmount = hasNotExpired
      ? limit.effectiveRemainingDailyAmount ?? limit.remainingDailyAmount
      : limit.effectiveMaximumDailyAmount ?? limit.maximumDailyDepositAmount;

    if (
      typeof maxAmountOverrideCents === "number" &&
      maxDepositAmount > maxAmountOverrideCents
    ) {
      return maxAmountOverrideCents;
    }

    return maxDepositAmount;
  } else {
    return null;
  }
};

export const validateDepositAmount = (
  value: any,
  limit: DepositLimit | undefined,
  loading: boolean,
  currency: string,
  shortMessage?: boolean,
  isProfileIomRegulatedMarketUnverified?: boolean,
) => {
  if (limit && !loading) {
    const amount = parseFloat(value) * 100;
    const expiry = parseISO(limit.expiresAt ?? "");
    const hasExpiry = isValid(expiry);
    const maximumAmount = getMaximumDepositAmount(limit);
    const depositAmountAtExpiry =
      limit.depositAmountAtExpiry ||
      (limit.effectiveMaximumDailyAmount ?? limit.maximumDailyDepositAmount);

    if (hasExpiry && isPast(expiry)) {
      return "Loading...";
    } else if (
      hasExpiry &&
      maximumAmount !== null &&
      maximumAmount < limit.minimumDepositAmount
    ) {
      return `${depositLimitReachedPrefix} ${constructCurrency(
        depositAmountAtExpiry,
        {
          currencyType: currency,
        },
      )} again in ${formatDistanceToNowStrict(expiry, { roundingMethod: "floor" })}${
        shortMessage ? `` : `. ${adjustLimitMessage}`
      }`;
    } else if (maximumAmount !== null && maximumAmount < amount) {
      return `Amount must be ${constructCurrency(maximumAmount, {
        currencyType: currency,
      })}, or less${isProfileIomRegulatedMarketUnverified ? ". Verify to increase your limits" : ""}`;
    } else if (limit.minimumDepositAmount > amount) {
      return (
        "Amount must be " +
        constructCurrency(limit.minimumDepositAmount, {
          currencyType: currency,
        }) +
        ", or more"
      );
    }
  }
  return true;
};
